import dayjs from "dayjs";
import { Link } from "react-router-dom";
import Loader from "../../../components/shared/Loader/Loader";
import arrowLeftDark from "../../../assets/images/icons/arrow-left-dark.svg";
import arrowRightDark from "../../../assets/images/icons/arrow-right-dark.svg";
import { ConfirmationForClassReservationPopup } from "../../../components/ConfirmationForClassReservationPopup/ConfirmationForClassReservationPopup";
import { BookedSuccessfully } from "../../../components/BookedSuccessfully/BookedSuccessfully";
import { useClassesTab } from "./useClassesTab";
import { ISettings } from "../../../models/Settings";
import { IUserSubscriptionModel } from "../../../models/UserSubscription";
import { useOpenGymsTab } from "./useOpenGymsTab";

export const OpenGymsCalendar = ({
  settings,
  gymId,
  userSubscription,
}: {
  settings: ISettings | null;
  gymId: string;
  userSubscription: IUserSubscriptionModel | null;
}) => {
  const today = dayjs().format("YYYY-MM-DD");
  const {
    classes,
    t,
    isLoading,
    selectedDate,
    selected,
    submitting,
    isConfirmVisible,
    isSuccessVisible,
    onSelect,
    onCloseConfirm,
    onConfirmBooking,
    onCloseSuccess,
    changeDate,
    isPrevDisabled,
    isNextDisabled,
    setIsUsingLoyalty,
  } = useOpenGymsTab({ settings, gymId, today });

  return (
    <div className="schedule-wrapper">
      {/* === HEADER === */}
      <div className="header">
        {!isPrevDisabled ? (
          <button className="nav-button" onClick={() => changeDate("prev")}>
            <img src={arrowLeftDark} alt="prev" />
          </button>
        ) : (
          <span />
        )}
        <span>
          {dayjs(selectedDate).isSame(today, "day")
            ? t("Today")
            : dayjs(selectedDate).format("ddd, MMM D")}
        </span>
        {!isNextDisabled ? (
          <button className="nav-button" onClick={() => changeDate("next")}>
            <img src={arrowRightDark} alt="next" />
          </button>
        ) : (
          <span />
        )}
      </div>

      {/* === LOADER === */}
      {isLoading && (
        <div
          style={{
            height: "200px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Loader />
        </div>
      )}

      {/* === CLASSES === */}
      {!isLoading &&
        classes.map((item) =>
          item.isAlreadyBooked ? null : (
            <div className="class-card" key={item.id}>
              <div className="left">
                <div className="wrapper">
                  <div className="time-box">
                    <span className="time">
                      {dayjs(new Date(item.occuranceDate)).format("HH:mm")}
                    </span>
                  </div>

                  <div className="class-info">
                    <p className="name">{item.name}</p>
                    <p>{item.roomName}</p>
                  </div>
                </div>
              </div>

              {userSubscription ? (
                <>
                  {item.isAvailable ? (
                    <button onClick={() => onSelect(item)} className="credits-button">
                      {item.credits} {t("Credits")}
                    </button>
                  ) : (
                    <div className="not-available">
                      <span className="not-available__text">{t("NotAvailable")}</span>
                    </div>
                  )}
                </>
              ) : (
                <Link className="buy-subscription" to={"/"}>
                  {t("BuySubscription")}
                </Link>
              )}
            </div>
          )
        )}

      {/* === NO CLASSES === */}
      {!isLoading && classes.length === 0 && (
        <div className="no-classes">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="120"
            height="120"
            viewBox="0 0 120 120"
            fill="none"
          >
            <g clip-path="url(#clip0_27463_19350)">
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M52.5003 10C55.4003 10 58.2503 10.29 60.9953 10.85C62.2949 11.1139 63.4364 11.8832 64.1687 12.9887C64.901 14.0943 65.1642 15.4454 64.9003 16.745C64.6364 18.0446 63.8671 19.1861 62.7615 19.9184C61.656 20.6507 60.3049 20.9139 59.0053 20.65C52.7077 19.3653 46.1707 19.9767 40.2206 22.4069C34.2705 24.8371 29.1745 28.977 25.577 34.3032C21.9796 39.6293 20.0421 45.9025 20.0096 52.3297C19.977 58.7569 21.8509 65.0494 25.3943 70.4116C28.9377 75.7739 33.9915 79.9652 39.9167 82.4555C45.8419 84.9458 52.3724 85.6233 58.6827 84.4024C64.9929 83.1815 70.7995 80.117 75.3682 75.5964C79.9369 71.0758 83.0627 65.302 84.3503 59.005C84.481 58.3615 84.7371 57.7501 85.104 57.2056C85.471 56.6611 85.9416 56.1942 86.489 55.8316C87.0364 55.469 87.6499 55.2178 88.2944 55.0922C88.9389 54.9667 89.6018 54.9693 90.2453 55.1C90.8888 55.2307 91.5002 55.4868 92.0447 55.8538C92.5892 56.2207 93.0561 56.6913 93.4187 57.2387C93.7813 57.7861 94.0325 58.3996 94.1581 59.0441C94.2836 59.6886 94.281 60.3515 94.1503 60.995C92.938 66.9599 90.4532 72.593 86.8653 77.51L85.8803 78.81L104.14 97.07C105.047 97.9677 105.575 99.1778 105.619 100.453C105.662 101.728 105.216 102.971 104.373 103.928C103.53 104.885 102.352 105.484 101.082 105.601C99.812 105.719 98.5449 105.346 97.5403 104.56L97.0703 104.14L78.8103 85.88C73.4875 90.0748 67.2388 92.9359 60.5854 94.2246C53.932 95.5132 47.0669 95.1921 40.5629 93.288C34.0588 91.3839 28.1044 87.952 23.1965 83.2786C18.2885 78.6053 14.5693 72.826 12.3492 66.4229C10.129 60.0199 9.47232 53.1787 10.4338 46.4702C11.3953 39.7617 13.9472 33.3804 17.8765 27.8588C21.8058 22.3371 26.9985 17.8351 33.0216 14.7285C39.0446 11.6218 45.7233 10.0006 52.5003 10ZM95.0003 5C95.9357 5 96.8523 5.26239 97.646 5.75736C98.4398 6.25232 99.0787 6.96001 99.4903 7.8L99.7303 8.385L100.38 10.275C101.066 12.2859 102.172 14.1282 103.623 15.6797C105.075 17.2313 106.839 18.4568 108.8 19.275L109.725 19.625L111.615 20.27C112.551 20.5893 113.371 21.179 113.972 21.9645C114.573 22.75 114.927 23.696 114.991 24.6829C115.054 25.6698 114.823 26.6533 114.327 27.5089C113.831 28.3646 113.093 29.054 112.205 29.49L111.615 29.73L109.725 30.38C107.714 31.066 105.872 32.1714 104.321 33.623C102.769 35.0745 101.544 36.8392 100.725 38.8L100.375 39.725L99.7303 41.615C99.4104 42.5507 98.8203 43.3705 98.0345 43.9708C97.2487 44.5711 96.3026 44.925 95.3157 44.9876C94.3289 45.0502 93.3456 44.8188 92.4902 44.3226C91.6349 43.8264 90.9459 43.0878 90.5103 42.2L90.2703 41.615L89.6203 39.725C88.9343 37.7141 87.8289 35.8718 86.3773 34.3203C84.9258 32.7687 83.1611 31.5432 81.2003 30.725L80.2753 30.375L78.3853 29.73C77.4493 29.4107 76.6291 28.821 76.0283 28.0355C75.4275 27.25 75.0731 26.304 75.0099 25.3171C74.9468 24.3302 75.1777 23.3467 75.6735 22.4911C76.1693 21.6354 76.9077 20.946 77.7953 20.51L78.3853 20.27L80.2753 19.62C82.2862 18.934 84.1284 17.8286 85.68 16.377C87.2316 14.9255 88.457 13.1608 89.2753 11.2L89.6253 10.275L90.2703 8.385C90.6072 7.39794 91.2443 6.54092 92.0924 5.93395C92.9406 5.32697 93.9573 5.00042 95.0003 5ZM95.0003 20.98C93.8173 22.4683 92.4686 23.817 90.9803 25C92.4703 26.18 93.8153 27.53 95.0003 29.02C96.1803 27.53 97.5303 26.18 99.0203 25C97.532 23.817 96.1833 22.4683 95.0003 20.98Z"
                fill="#FF8C42"
              />
            </g>
            <defs>
              <clipPath id="clip0_27463_19350">
                <rect width="120" height="120" fill="white" />
              </clipPath>
            </defs>
          </svg>
          <p className="no-classes__text">{t("ThereIsNoClassesAvailableYet")}</p>
        </div>
      )}

      {/* === CONFIRMATION MODAL === */}
      <ConfirmationForClassReservationPopup
        open={isConfirmVisible}
        title={selected?.name || ""}
        startISO={selected?.occuranceDate || ""}
        refundCancellationHours={settings?.refundCancellationHours || 0}
        onClose={onCloseConfirm}
        onConfirm={onConfirmBooking}
        credits={selected?.credits}
        loading={submitting}
        isLoyaltyDealAvailable={selected?.isLoyaltyDealAvailable || false}
        setIsUsingLoyalty={setIsUsingLoyalty}
      />

      {/* === SUCCESS MODAL === */}
      <BookedSuccessfully isOpenGym={true} open={isSuccessVisible} onClose={onCloseSuccess} />
    </div>
  );
};
